import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Input, Select, Space, Typography, Row, Col, Skeleton, ConfigProvider, Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showVisumEffect } from "../../components/utils/antWaveEffect";
import { toast } from "../../components/toasts/toastStore";
import { getUsuarios, esAdministrador } from "./usuariosApi";
import "./PaquetesPage.css";
import { usePermisos } from "../../context/PermisosContext";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%)";

const isActive = (u) => u.active === 1 || u.active === true;

const ORDEN_OPTIONS = [
  { value: "nombre_asc", label: "Nombre (A-Z)" },
  { value: "nombre_desc", label: "Nombre (Z-A)" },
  { value: "rol", label: "Rol" },
  { value: "activos", label: "Activos primero" },
];

const byNombre = (a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" });

const ORDENADORES = {
  nombre_asc: byNombre,
  nombre_desc: (a, b) => byNombre(b, a),
  rol: (a, b) => (a.rol || "").localeCompare(b.rol || "", "es", { sensitivity: "base" }) || byNombre(a, b),
  activos: (a, b) => Number(isActive(b)) - Number(isActive(a)) || byNombre(a, b),
};

export default function UsuariosPage() {
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };
  const esAdmin = esAdministrador();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [orden, setOrden] = useState("nombre_asc");

  useEffect(() => {
    if (!esAdmin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getUsuarios()
      .then((res) => setItems(res.data?.items ?? res.data ?? []))
      .catch((err) => toast.error(err?.response?.data?.detail || "No se pudieron cargar los usuarios"))
      .finally(() => setLoading(false));
  }, [esAdmin]);

  const filteredItems = useMemo(() => {
    let list = items;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((u) =>
        (u.nombre || "").toLowerCase().includes(q) ||
        (u.correo || u.usuario || "").toLowerCase().includes(q)
      );
    }
    return list.slice().sort(ORDENADORES[orden] || byNombre);
  }, [items, search, orden]);

  if (!esAdmin) {
    return (
      <main className="paq-main">
        <div className="paq-content">
          <div className="paq-empty" style={{ marginTop: 40 }}>
            <i className="fa-solid fa-lock" style={{ fontSize: 32, color: "#94a3b8", marginBottom: 12, display: "block" }} />
            <div style={{ fontSize: 16, fontWeight: 700, color: "#334155", marginBottom: 4 }}>Acceso restringido</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>
              Solo un usuario con rol Administrador puede ver y gestionar cuentas, roles y permisos.
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <ConfigProvider wave={{ showEffect: showVisumEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Usuarios</Title>
                <Text className="paq-subtitle">Cuentas del sistema, roles y permisos por módulo.</Text>
              </Space>
              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o correo..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>

              <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
                <Col xs={24} lg={6}>
                  <div className="paq-actions">
                    <Button
                      className="paq-btn-clean"
                      onClick={() => {
                        setSearch("");
                        setOrden("nombre_asc");
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Usuarios ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} usuarios encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  <Select
                    value={orden}
                    onChange={setOrden}
                    options={ORDEN_OPTIONS}
                    className="paq-orden-select"
                    suffixIcon={<i className="fa-solid fa-arrow-down-wide-short" />}
                  />
                  {perm("usuarios", "insertar") && (
                    <Button
                    type="primary"
                    icon={<i className="fa-solid fa-plus" />}
                    onClick={() => navigate("/app/usuarios/crear")}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                  >
                    Nuevo usuario
                  </Button>
                  )}
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <Empty description="Sin usuarios que coincidan con los filtros" />
                </div>
              )}

              <div className="paq-category-block">
                <div className="paq-grid">
                  {loading && Array.from({ length: 4 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                  {!loading && filteredItems.map((u) => (
                    <div key={u.id_user || u.id} className="paq-card">
                      <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                        <i className="fa-solid fa-user paq-card-banner-icon" />
                      </div>
                      <div className="paq-card-header">
                        <div className="paq-card-head">
                          <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{u.nombre}</Paragraph>
                          <span className={`paq-tipo-badge ${isActive(u) ? "paq-tipo-decoracion" : "paq-tipo-barra"}`}>
                            {isActive(u) ? "Activo" : "Inactivo"}
                          </span>
                        </div>
                      </div>
                      <div className="paq-card-body">
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-at paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Correo</span>
                            <span className="paq-info-value">{u.correo || u.usuario}</span>
                          </div>
                        </div>
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-user-shield paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Rol</span>
                            <span className="paq-info-value">{u.rol || "Sin rol"}</span>
                          </div>
                        </div>
                      </div>
                      <div className="paq-card-footer">
                        <Button
                          className="paq-btn-details"
                          icon={<i className="fa-solid fa-circle-info" />}
                          onClick={() => navigate(`/app/usuarios/${u.id_user || u.id}`)}
                        >
                          Ver detalles
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
